import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowUpRight, RefreshCw, UserCheck, UserRound } from "lucide-react";
import { api, patch } from "../api";
import type { Row } from "../api";
import { displayReturnId, RETURN_COPY, taxText } from "../terminology";
import { Badge, Button, Callout, Modal } from "../ui";
import { DataTable } from "./DataTable";
import { useFormValidation } from "../form-validation";
import { workspaceOperationError } from "../presentation";
import { FieldError, ValidationSummary } from "./ValidationSummary";
import "./case-assignments.css";

export interface CaseAssignmentsProps {
  cases: Row[];
  user: Row;
  onOpen: (record: Row) => void;
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  returnDisplayIds?: ReadonlyMap<string, string>;
}
export interface AssigneeOption {
  id: string;
  label: string;
  disabled: boolean;
}
export interface AssignmentError {
  code: string;
  status: number | null;
  message: string;
  confirmedSaved?: boolean;
  operation?: "save" | "load";
}

export function canManageAssignments(user: Row): boolean {
  return user?.role === "workspace_admin" || user?.role === "preparer";
}

/** The directory is workspace-scoped; never infer users from email or prior-case IDs. */
export function assigneeOptions(
  members: Row[],
  user: Row,
  record?: Row | null,
): AssigneeOption[] {
  const options = new Map<string, AssigneeOption>();
  options.set("", { id: "", label: "Unassigned", disabled: false });
  if (typeof user?.id === "string" && user.id)
    options.set(user.id, {
      id: user.id,
      label: `${user.name || "You"} (you)`,
      disabled: false,
    });
  for (const member of members) {
    if (
      typeof member?.id !== "string" ||
      !member.id ||
      typeof member.name !== "string"
    )
      continue;
    options.set(member.id, {
      id: member.id,
      label: `${member.name}${member.id === user.id ? " (you)" : ""}`,
      disabled: false,
    });
  }
  if (record?.assigneeId && !options.has(String(record.assigneeId)))
    options.set(String(record.assigneeId), {
      id: String(record.assigneeId),
      label: `${record.assignee || "Current assignee"} · current assignment`,
      disabled: true,
    });
  return [...options.values()];
}

export function assignmentRequest(
  record: Row,
  assigneeId: string,
  user: Row,
  options: AssigneeOption[],
) {
  if (!canManageAssignments(user))
    throw Object.assign(
      new Error(
        "Your workspace role does not permit changing return assignments.",
      ),
      { code: "FORBIDDEN", status: 403 },
    );
  if (
    typeof record?.id !== "string" ||
    !record.id ||
    !Number.isSafeInteger(record.revision) ||
    record.revision < 1
  ) {
    throw Object.assign(
      new Error(
        "Refresh the return before assigning it; an exact return revision is required.",
      ),
      { code: "REVISION_REQUIRED", status: 428 },
    );
  }
  if (!options.some((option) => option.id === assigneeId && !option.disabled)) {
    throw Object.assign(
      new Error(
        "Choose an available member of this workspace or leave the return unassigned.",
      ),
      { code: "INVALID_ASSIGNEE", status: 422 },
    );
  }
  return {
    caseId: record.id as string,
    url: `/cases/${encodeURIComponent(record.id)}`,
    body: {
      expectedRevision: record.revision,
      assigneeId: assigneeId === "" ? null : assigneeId,
    },
  };
}

export function assignmentError(error: unknown): AssignmentError {
  const value = error && typeof error === "object" ? (error as Row) : {};
  return {
    code: typeof value.code === "string" ? value.code : "REQUEST_FAILED",
    status: typeof value.status === "number" ? value.status : null,
    message:
      typeof value.message === "string"
        ? value.message
        : "The assignment could not be saved. Try again after refreshing the return.",
  };
}

/** No success callback runs until the write has returned a genuine committed response. */
export async function saveAssignment(
  request: ReturnType<typeof assignmentRequest>,
  submit: (url: string, body: unknown) => Promise<Row> = patch,
): Promise<Row> {
  const result = await submit(request.url, request.body);
  if (
    !result ||
    result.id !== request.caseId ||
    !Number.isSafeInteger(result.revision) ||
    result.revision <= request.body.expectedRevision ||
    result.assigneeId !== request.body.assigneeId
  ) {
    throw Object.assign(
      new Error(
        "The server response could not confirm this assignment. Refresh before retrying; the write may already have committed.",
      ),
      { code: "ASSIGNMENT_CONFIRMATION_MISSING", status: null },
    );
  }
  return result;
}

function ErrorNotice({ error }: { error: AssignmentError }) {
  return (
    <div className="assignment-error" role="alert">
      <strong>
        {error.confirmedSaved
          ? "Assignment saved; refresh needed"
          : error.operation === "load"
            ? "Assignments could not be loaded"
            : "Assignment needs attention"}
      </strong>
      <p>{assignmentErrorMessage(error)}</p>
    </div>
  );
}

export function assignmentErrorMessage(error: AssignmentError): string {
  if (error.confirmedSaved)
    return "The assignment was saved, but the latest workspace information could not be loaded. Refresh before making another change.";
  if (error.operation === "load")
    return workspaceOperationError(error, "load return assignments");
  if (error.code === "REVISION_CONFLICT")
    return "This return changed while you were assigning it. This request did not overwrite the newer assignment. Refresh, review the current assignee, and choose again.";
  if (error.code === "INVALID_ASSIGNEE")
    return "Workspace member: choose a currently available member of this workspace or Unassigned.";
  if (error.code === "REVISION_REQUIRED")
    return "Refresh the return to load its current assignment before saving.";
  return workspaceOperationError(error, "save the return assignment");
}

export function CaseAssignments({
  cases,
  user,
  onOpen,
  refresh,
  notify,
  returnDisplayIds,
}: CaseAssignmentsProps) {
  const writable = canManageAssignments(user);
  const [members, setMembers] = useState<Row[]>([]),
    [directoryLoading, setDirectoryLoading] = useState(false),
    [directoryError, setDirectoryError] = useState<AssignmentError | null>(
      null,
    );
  const [editing, setEditing] = useState<Row | null>(null),
    [selectedId, setSelectedId] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<AssignmentError | null>(null);
  const [directoryVersion, setDirectoryVersion] = useState(0);
  const savePending = useRef(false);
  const memberId = `${useId()}-member`;
  useEffect(() => {
    let cancelled = false;
    setMembers([]);
    setDirectoryError(null);
    if (!writable) {
      setDirectoryLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setDirectoryLoading(true);
    // /members is explicitly permitted for authenticated members. Never call /admin/users here.
    api<Row[]>("/members")
      .then((result) => {
        if (!Array.isArray(result))
          throw new Error(
            "The workspace member directory returned an unexpected response.",
          );
        if (!cancelled) setMembers(result);
      })
      .catch((reason) => {
        if (!cancelled) setDirectoryError(assignmentError(reason));
      })
      .finally(() => {
        if (!cancelled) setDirectoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [writable, user.id, user.role, directoryVersion]);
  const options = useMemo(
    () => assigneeOptions(members, user, editing),
    [members, user, editing],
  );
  const validation = useFormValidation(
    "Return assignment",
    editing
      ? [
          {
            id: memberId,
            label: "Workspace member",
            value: selectedId,
            validate: (value) =>
              error?.code === "INVALID_ASSIGNEE"
                ? "This member is no longer available for assignment. Choose another workspace member or Unassigned."
                : options.some(
                      (option) => option.id === value && !option.disabled,
                    )
                  ? undefined
                  : "Choose an available workspace member or Unassigned.",
          },
        ]
      : [],
  );
  const close = useCallback(() => {
    if (!savePending.current) {
      setEditing(null);
      setError(null);
    }
  }, []);
  function edit(record: Row) {
    if (!writable || savePending.current) return;
    setEditing(record);
    setSelectedId(String(record.assigneeId || ""));
    setError(null);
  }
  async function refreshCases() {
    if (savePending.current) return;
    setBusy(true);
    setError(null);
    try {
      await refresh();
      setEditing(null);
      setDirectoryVersion((value) => value + 1);
    } catch (reason) {
      setError({ ...assignmentError(reason), operation: "load" });
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!editing || savePending.current) return;
    if (!validation.validate()) return;
    setError(null);
    let committed = false;
    try {
      const request = assignmentRequest(editing, selectedId, user, options);
      savePending.current = true;
      setBusy(true);
      await saveAssignment(request);
      committed = true;
      setEditing(null);
      await refresh();
      notify(
        selectedId ? "Return assignment saved." : "Return is now unassigned.",
      );
    } catch (reason) {
      const problem = assignmentError(reason);
      setError(committed ? { ...problem, confirmedSaved: true } : problem);
      if (committed)
        notify(
          "Assignment saved. Refresh the workspace to see the latest return.",
        );
    } finally {
      savePending.current = false;
      setBusy(false);
    }
  }
  const assigned = cases.filter((record) => Boolean(record.assigneeId)).length;
  return (
    <div className="case-assignments">
      <div className="assignment-summary">
        <div>
          <span className="assignment-mark">
            <UserCheck size={21} />
          </span>
          <div>
            <strong>Clear ownership. Connected work.</strong>
            <p>
              {assigned} assigned · {cases.length - assigned} unassigned
            </p>
          </div>
        </div>
        <Button
          disabled={busy}
          onClick={() => {
            void refreshCases();
          }}
        >
          <RefreshCw size={15} />
          Refresh
        </Button>
      </div>
      {!writable && (
        <Callout title="Read-only assignments">
          Your {user.role || "current"} role can inspect and open returns. A
          workspace administrator or preparer must change their assignments.
        </Callout>
      )}
      {directoryError && writable && (
        <Callout title="Workspace directory unavailable">
          {workspaceOperationError(directoryError, "load workspace members")}{" "}
          You can still choose yourself or unassigned. Existing assignments are
          retained until a confirmed save.
        </Callout>
      )}
      {error && !editing && <ErrorNotice error={error} />}
      <DataTable
        records={cases}
        getRowId={(record) => String(record.id)}
        getSearchText={(record) => displayReturnId(record, returnDisplayIds)}
        title="Return assignments"
        emptyMessage="No returns yet. Create a synthetic return to begin assigning work."
        onInspect={onOpen}
        columns={[
          {
            key: "name",
            label: "Return",
            render: (_, record) => (
              <div className="record-cell">
                <span className="record-avatar">
                  {String(record.name || "R").slice(0, 1)}
                </span>
                <span>
                  <strong>{String(record.name || "")}</strong>
                  <small>{displayReturnId(record, returnDisplayIds)}</small>
                </span>
              </div>
            ),
          },
          {
            key: "assignee",
            label: "Assigned to",
            render: (value, record) => (
              <span className="assignment-person">
                <UserRound size={15} />
                {record.assigneeId
                  ? String(value || "Assigned member")
                  : "Unassigned"}
              </span>
            ),
          },
          {
            key: "status",
            label: RETURN_COPY.status,
            render: (value) => <Badge>{taxText(value || "DRAFT")}</Badge>,
          },
          { key: "revision", label: "Revision" },
          {
            key: "updatedAt",
            label: "Updated",
            render: (value) =>
              value
                ? new Date(String(value)).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                : "—",
          },
          {
            key: "assignmentActions",
            label: "Actions",
            sortable: false,
            render: (_, record) => (
              <div className="assignment-actions">
                <Button onClick={() => onOpen(record)}>
                  Open Return
                  <ArrowUpRight size={14} />
                </Button>
                {writable && (
                  <Button onClick={() => edit(record)} disabled={busy}>
                    Assign Return
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {editing && (
        <Modal title="Assign Return" close={close}>
          <div className="detail-body">
            <div className="assignment-case-label">
              <strong>{String(editing.name)}</strong>
              <span>{displayReturnId(editing, returnDisplayIds)}</span>
            </div>
            <ValidationSummary issues={validation.issues} />
            <label htmlFor={memberId}>
              Workspace member
              <select
                {...validation.fieldProps(memberId)}
                aria-label="Workspace member"
                value={selectedId}
                disabled={busy || !writable}
                onChange={(event) => {
                  setSelectedId(event.target.value);
                  setError(null);
                }}
              >
                {options.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <FieldError issue={validation.issue(memberId)} />
            </label>
            {directoryLoading && (
              <p role="status">Loading current workspace members…</p>
            )}
            <p className="assignment-hint">
              This changes local preparation ownership only. It does not grant
              review, release, or certification authority.
            </p>
            {error && <ErrorNotice error={error} />}
            <div className="form-actions">
              {error?.code === "REVISION_CONFLICT" ? (
                <Button
                  disabled={busy}
                  onClick={() => {
                    void refreshCases();
                  }}
                >
                  Refresh and close
                </Button>
              ) : (
                <Button disabled={busy} onClick={close}>
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                disabled={
                  busy ||
                  !writable ||
                  selectedId === String(editing.assigneeId || "")
                }
                onClick={() => {
                  void save();
                }}
              >
                {busy ? "Saving…" : "Save assignment"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
